import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Truck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import logoIcon from '../assets/logo-icon.png';
import logoWordmark from '../assets/logo-wordmark.png';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      toast.success('¡Bienvenido de nuevo!');
      
      // Redirect based on role
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex" data-testid="login-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('/images/family-car.jpg')` 
          }}
        />
        {/* Gradient overlay - dark at the bottom for text readability,
            lighter at the top so the family is clearly visible */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(14,31,56,0.15) 0%, rgba(14,31,56,0.35) 55%, rgba(14,31,56,0.85) 100%)',
          }}
        />
        <div className="absolute inset-x-0 bottom-0 p-12">
          <div className="text-white max-w-md">
            <h2 className="font-heading text-4xl font-bold uppercase tracking-tight mb-4 drop-shadow-lg">
              Tu próximo vehículo te espera
            </h2>
            <p className="text-white/95 text-lg drop-shadow-md">
              Miles de familias y empresas confían en Vehimax para encontrar el vehículo ideal. Inicia sesión y continúa tu búsqueda.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <img
                src={logoIcon}
                alt=""
                aria-hidden="true"
                className="h-14 w-auto"
              />
              <img
                src={logoWordmark}
                alt="VehiMax - Todos los Vehículos. Una Plataforma"
                className="h-8 w-auto rounded-sm"
              />
            </Link>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Iniciar sesión</h1>
            <p className="text-muted-foreground mt-2">Accede a tu cuenta de Vehimax</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wide">Correo electrónico</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
                placeholder="john@example.com"
                data-testid="login-email"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wide">Contraseña</Label>
                <Link to="/forgot-password" className="text-sm text-accent hover:underline">¿Olvidaste tu contraseña?</Link>
              </div>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12"
                placeholder="••••••••"
                testId="login-password"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="btn-accent w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            {/* Register Link */}
            <p className="text-center text-sm text-muted-foreground">
              ¿No tienes una cuenta?{' '}
              <Link to="/register" className="text-accent font-semibold hover:underline" data-testid="register-link">Crear cuenta</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
